// Phase 4b: chip-based filter toolbar for the Orders page.
//
// Renders the Add-filter affordance + the active chips in one flex row.
// Drives the unified OrderFilterState model from @/lib/order-filters.
// Replaces the legacy KPI card band, the date dropdown, the
// "All Fulfillment Types" dropdown, and the half-merged chip row in
// seller-dashboard.tsx. After 4b, all filter surfaces write filterState
// directly — the Phase 4a derived projections + adapter setters have been
// removed from seller-dashboard.tsx.
//
// Chip behavior (locked decisions in phase-4-brief.md):
//   - Multi-select axes (Status, Fulfillment type, QuickBooks): chips stack;
//     adding a chip never removes another. Within an axis OR; across axes AND.
//   - Order Window is single-select: picking a different window REPLACES the
//     active window chip; re-picking the active window toggles it off.
//   - Clicking the chip body opens its editor (the same value picker the
//     Add-filter menu uses), pre-filled with current state.
//   - Clicking ✕ removes only that chip's selection.
//   - Clear all wipes every chip including the Order Window chip.
//   - Uniform neutral style for all chips (chips are controls; the status
//     pill / QB mark inside table rows are data).

import * as React from "react";
import { Plus, X as XIcon, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";

import {
  EMPTY_FILTER_STATE,
  isFilterStateEmpty,
  type DateFilter,
  type FulfillmentTypeFilter,
  type OrderFilterState,
  type OrderSourceFilter,
  type OrderStatus,
  type QbSyncFilter,
} from "@/lib/order-filters";

export interface OrderWindowOption {
  value: string;   // YYYY-MM-DD
  label: string;   // display label, e.g. "Today, May 14"
  count: number;
}

interface OrdersFilterToolbarProps {
  filterState: OrderFilterState;
  onChange: (next: OrderFilterState) => void;
  showFulfillmentTypes: boolean;
  showOrderWindow: boolean;
  /** Offer "Kiosk" as an order source: kiosk mode is on, or a kiosk order is on the page. */
  showKioskSource: boolean;
  availableWindows: OrderWindowOption[];
  unscheduledCount: number;
}

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: "Pending",
  reviewed: "Reviewed",
  cancelled: "Cancelled",
};

const FULFILLMENT_TYPE_LABEL: Record<FulfillmentTypeFilter, string> = {
  delivery: "Delivery",
  pickup: "Pickup",
};

const QB_LABEL: Record<QbSyncFilter, string> = {
  sent: "Sent to QuickBooks",
  "not-sent": "Not sent to QuickBooks",
};

const SOURCE_LABEL: Record<OrderSourceFilter, string> = {
  customer_app: "Customer app",
  kiosk: "Kiosk",
  admin_manual: "Admin entered",
  sms_agent: "Text (SMS agent)",
};

function dateChipLabel(
  date: NonNullable<DateFilter>,
  windows: OrderWindowOption[]
): string {
  if (date.kind === "unscheduled") return "Unscheduled";
  // Prefer the window's display label so chip reads "Today, May 14" not raw YYYY-MM-DD.
  const match = windows.find(w => w.value === date.value);
  return match?.label ?? date.value;
}

// ─── Value pickers ──────────────────────────────────────────────────────────

function CheckboxList<T extends string>({
  options,
  labels,
  value,
  onChange,
  minWidth,
}: {
  options: readonly T[];
  labels: Record<T, string>;
  value: T[];
  onChange: (next: T[]) => void;
  minWidth?: number;
}) {
  return (
    <div className="p-1" style={minWidth ? { minWidth } : undefined}>
      {options.map(opt => {
        const checked = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() =>
              onChange(checked ? value.filter(v => v !== opt) : [...value, opt])
            }
            className="flex w-full items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-sm text-left"
            data-testid={`filter-option-${opt}`}
          >
            <Checkbox checked={checked} className="pointer-events-none" />
            <span>{labels[opt]}</span>
          </button>
        );
      })}
    </div>
  );
}

const STATUS_OPTIONS: readonly OrderStatus[] = ["pending", "reviewed", "cancelled"];
const FULFILLMENT_TYPE_OPTIONS: readonly FulfillmentTypeFilter[] = ["delivery", "pickup"];
const QB_OPTIONS: readonly QbSyncFilter[] = ["sent", "not-sent"];
const SOURCE_OPTIONS: readonly OrderSourceFilter[] = ["customer_app", "kiosk", "admin_manual", "sms_agent"];

/**
 * Kiosk mode is a paid add-on that is off for most vendors, and "Kiosk" is not offered
 * as a source to a vendor that has neither the add-on nor a kiosk order to find. An
 * active kiosk filter always keeps its option, so it can be seen and removed.
 */
function sourceOptions(showKioskSource: boolean, selected: readonly OrderSourceFilter[]): readonly OrderSourceFilter[] {
  return showKioskSource || selected.includes("kiosk") ? SOURCE_OPTIONS : SOURCE_OPTIONS.filter(o => o !== "kiosk");
}

function OrderWindowPicker({
  value,
  onChange,
  windows,
  unscheduledCount,
}: {
  value: DateFilter;
  onChange: (next: DateFilter) => void;
  windows: OrderWindowOption[];
  unscheduledCount: number;
}) {
  // Single-select with toggle: clicking the active option clears it.
  const pick = (next: DateFilter) => {
    if (
      value &&
      next &&
      ((value.kind === "specific" && next.kind === "specific" && value.value === next.value) ||
        (value.kind === "unscheduled" && next.kind === "unscheduled"))
    ) {
      onChange(null);
    } else {
      onChange(next);
    }
  };

  const isActive = (opt: OrderWindowOption) =>
    value?.kind === "specific" && value.value === opt.value;
  const unscheduledActive = value?.kind === "unscheduled";

  return (
    <div className="p-1 min-w-[220px] max-h-[320px] overflow-y-auto">
      {windows.length === 0 && unscheduledCount === 0 && (
        <div className="px-3 py-2 text-xs text-gray-500">No order windows</div>
      )}
      {windows.map(opt => {
        const active = isActive(opt);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => pick({ kind: "specific", value: opt.value })}
            className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 rounded text-sm text-left ${
              active ? "bg-gray-100 font-medium" : "hover:bg-gray-100"
            }`}
            data-testid={`filter-window-${opt.value}`}
          >
            <span>{opt.label}</span>
            <span className="text-xs text-gray-500">{opt.count}</span>
          </button>
        );
      })}
      {unscheduledCount > 0 && (
        <button
          type="button"
          onClick={() => pick({ kind: "unscheduled" })}
          className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 rounded text-sm text-left ${
            unscheduledActive ? "bg-gray-100 font-medium" : "hover:bg-gray-100"
          }`}
          data-testid="filter-window-unscheduled"
        >
          <span>Unscheduled</span>
          <span className="text-xs text-gray-500">{unscheduledCount}</span>
        </button>
      )}
    </div>
  );
}

// ─── Add-filter menu ───────────────────────────────────────────────────────

function AddFilterButton({
  filterState,
  onChange,
  showFulfillmentTypes,
  showOrderWindow,
  showKioskSource,
  availableWindows,
  unscheduledCount,
}: {
  filterState: OrderFilterState;
  onChange: (next: OrderFilterState) => void;
  showFulfillmentTypes: boolean;
  showOrderWindow: boolean;
  showKioskSource: boolean;
  availableWindows: OrderWindowOption[];
  unscheduledCount: number;
}) {
  // Uses DropdownMenu + DropdownMenuSub so on desktop the per-axis sub-menu
  // opens on hover and renders to the right of the axis list (Radix handles
  // positioning + collision). Multi-select axes use DropdownMenuCheckboxItem
  // with onSelect.preventDefault() so toggling doesn't close the menu —
  // single-toggle picks (Order Window) use DropdownMenuItem and let the
  // default "close on select" close the menu, which is the right ergonomic.

  const toggleStatus = (s: OrderStatus, checked: boolean) =>
    onChange({
      ...filterState,
      statuses: checked
        ? [...filterState.statuses, s]
        : filterState.statuses.filter(v => v !== s),
    });

  const toggleFulfillmentType = (t: FulfillmentTypeFilter, checked: boolean) =>
    onChange({
      ...filterState,
      fulfillmentTypes: checked
        ? [...filterState.fulfillmentTypes, t]
        : filterState.fulfillmentTypes.filter(v => v !== t),
    });

  const toggleQb = (q: QbSyncFilter, checked: boolean) =>
    onChange({
      ...filterState,
      quickbooks: checked
        ? [...filterState.quickbooks, q]
        : filterState.quickbooks.filter(v => v !== q),
    });

  const toggleSource = (s: OrderSourceFilter, checked: boolean) =>
    onChange({
      ...filterState,
      sources: checked
        ? [...filterState.sources, s]
        : filterState.sources.filter(v => v !== s),
    });

  // Single-select Order Window with toggle-off when re-picking the active value.
  const pickWindow = (next: Exclude<typeof filterState.date, null>) => {
    const same =
      (filterState.date?.kind === "specific" &&
        next.kind === "specific" &&
        filterState.date.value === next.value) ||
      (filterState.date?.kind === "unscheduled" && next.kind === "unscheduled");
    onChange({ ...filterState, date: same ? null : next });
  };

  const windowIsActive = (value: string) =>
    filterState.date?.kind === "specific" && filterState.date.value === value;
  const unscheduledIsActive = filterState.date?.kind === "unscheduled";

  const axisBadge = (count: number) =>
    count > 0 ? <span className="ml-auto text-xs text-gray-500">{count}</span> : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs bg-white border-gray-200 gap-1.5"
          data-testid="button-add-filter"
        >
          <Plus className="h-3.5 w-3.5" />
          Add filter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid="filter-axis-status">
            <span>Status</span>
            {axisBadge(filterState.statuses.length)}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-[180px]">
            {STATUS_OPTIONS.map(opt => (
              <DropdownMenuCheckboxItem
                key={opt}
                checked={filterState.statuses.includes(opt)}
                onCheckedChange={c => toggleStatus(opt, c === true)}
                onSelect={e => e.preventDefault()}
                data-testid={`filter-option-${opt}`}
              >
                {STATUS_LABEL[opt]}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {showFulfillmentTypes && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="filter-axis-fulfillmentType">
              <span>Fulfillment type</span>
              {axisBadge(filterState.fulfillmentTypes.length)}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[160px]">
              {FULFILLMENT_TYPE_OPTIONS.map(opt => (
                <DropdownMenuCheckboxItem
                  key={opt}
                  checked={filterState.fulfillmentTypes.includes(opt)}
                  onCheckedChange={c => toggleFulfillmentType(opt, c === true)}
                  onSelect={e => e.preventDefault()}
                  data-testid={`filter-option-${opt}`}
                >
                  {FULFILLMENT_TYPE_LABEL[opt]}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid="filter-axis-quickbooks">
            <span>QuickBooks</span>
            {axisBadge(filterState.quickbooks.length)}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-[220px]">
            {QB_OPTIONS.map(opt => (
              <DropdownMenuCheckboxItem
                key={opt}
                checked={filterState.quickbooks.includes(opt)}
                onCheckedChange={c => toggleQb(opt, c === true)}
                onSelect={e => e.preventDefault()}
                data-testid={`filter-option-${opt}`}
              >
                {QB_LABEL[opt]}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid="filter-axis-source">
            <span>Order source</span>
            {axisBadge(filterState.sources.length)}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-[180px]">
            {sourceOptions(showKioskSource, filterState.sources).map(opt => (
              <DropdownMenuCheckboxItem
                key={opt}
                checked={filterState.sources.includes(opt)}
                onCheckedChange={c => toggleSource(opt, c === true)}
                onSelect={e => e.preventDefault()}
                data-testid={`filter-option-${opt}`}
              >
                {SOURCE_LABEL[opt]}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {showOrderWindow && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="filter-axis-date">
              <span>Order Window</span>
              {filterState.date && <span className="ml-auto text-xs text-gray-500">●</span>}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[220px] max-h-[320px] overflow-y-auto">
              {availableWindows.length === 0 && unscheduledCount === 0 && (
                <div className="px-3 py-2 text-xs text-gray-500">No order windows</div>
              )}
              {availableWindows.map(opt => {
                const active = windowIsActive(opt.value);
                return (
                  <DropdownMenuItem
                    key={opt.value}
                    onSelect={() => pickWindow({ kind: "specific", value: opt.value })}
                    data-testid={`filter-window-${opt.value}`}
                  >
                    {active && <Check className="mr-1 h-3 w-3 text-gray-600" />}
                    <span className={active ? "font-medium" : ""}>{opt.label}</span>
                    <span className="ml-auto text-xs text-gray-500">{opt.count}</span>
                  </DropdownMenuItem>
                );
              })}
              {unscheduledCount > 0 && (
                <DropdownMenuItem
                  onSelect={() => pickWindow({ kind: "unscheduled" })}
                  data-testid="filter-window-unscheduled"
                >
                  {unscheduledIsActive && <Check className="mr-1 h-3 w-3 text-gray-600" />}
                  <span className={unscheduledIsActive ? "font-medium" : ""}>Unscheduled</span>
                  <span className="ml-auto text-xs text-gray-500">{unscheduledCount}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Filter chip ───────────────────────────────────────────────────────────

const CHIP_CLASS =
  "inline-flex items-stretch overflow-hidden rounded-full border border-gray-200 bg-gray-100 text-gray-700";
const CHIP_BODY_CLASS =
  "px-2.5 py-1 text-xs font-medium hover:bg-gray-200 transition-colors";
const CHIP_X_CLASS =
  "px-1.5 py-1 hover:bg-gray-200 hover:text-gray-900 transition-colors flex items-center";

function FilterChip({
  label,
  editor,
  onRemove,
  testId,
}: {
  label: string;
  editor: React.ReactNode;
  onRemove: () => void;
  testId?: string;
}) {
  return (
    <span className={CHIP_CLASS} data-testid={testId}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={CHIP_BODY_CLASS}
            data-testid={testId ? `${testId}-body` : undefined}
          >
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto">
          {editor}
        </PopoverContent>
      </Popover>
      <button
        type="button"
        className={CHIP_X_CLASS}
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        data-testid={testId ? `${testId}-remove` : undefined}
      >
        <XIcon className="h-3 w-3" />
      </button>
    </span>
  );
}

// ─── Main toolbar ──────────────────────────────────────────────────────────

export function OrdersFilterToolbar({
  filterState,
  onChange,
  showFulfillmentTypes,
  showOrderWindow,
  showKioskSource,
  availableWindows,
  unscheduledCount,
}: OrdersFilterToolbarProps) {
  const hasActiveFilters = !isFilterStateEmpty(filterState);

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="orders-filter-toolbar">
      <AddFilterButton
        filterState={filterState}
        onChange={onChange}
        showFulfillmentTypes={showFulfillmentTypes}
        showOrderWindow={showOrderWindow}
        showKioskSource={showKioskSource}
        availableWindows={availableWindows}
        unscheduledCount={unscheduledCount}
      />

      {hasActiveFilters && (
        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium ml-1">
          Filters:
        </span>
      )}

      {filterState.statuses.map(s => (
        <FilterChip
          key={`status-${s}`}
          label={STATUS_LABEL[s]}
          testId={`chip-status-${s}`}
          editor={
            <CheckboxList
              options={STATUS_OPTIONS}
              labels={STATUS_LABEL}
              value={filterState.statuses}
              onChange={v => onChange({ ...filterState, statuses: v })}
              minWidth={180}
            />
          }
          onRemove={() =>
            onChange({ ...filterState, statuses: filterState.statuses.filter(v => v !== s) })
          }
        />
      ))}

      {filterState.fulfillmentTypes.map(t => (
        <FilterChip
          key={`ftype-${t}`}
          label={FULFILLMENT_TYPE_LABEL[t]}
          testId={`chip-fulfillment-${t}`}
          editor={
            <CheckboxList
              options={FULFILLMENT_TYPE_OPTIONS}
              labels={FULFILLMENT_TYPE_LABEL}
              value={filterState.fulfillmentTypes}
              onChange={v => onChange({ ...filterState, fulfillmentTypes: v })}
              minWidth={160}
            />
          }
          onRemove={() =>
            onChange({
              ...filterState,
              fulfillmentTypes: filterState.fulfillmentTypes.filter(v => v !== t),
            })
          }
        />
      ))}

      {filterState.quickbooks.map(q => (
        <FilterChip
          key={`qb-${q}`}
          label={QB_LABEL[q]}
          testId={`chip-quickbooks-${q}`}
          editor={
            <CheckboxList
              options={QB_OPTIONS}
              labels={QB_LABEL}
              value={filterState.quickbooks}
              onChange={v => onChange({ ...filterState, quickbooks: v })}
              minWidth={220}
            />
          }
          onRemove={() =>
            onChange({ ...filterState, quickbooks: filterState.quickbooks.filter(v => v !== q) })
          }
        />
      ))}

      {filterState.sources.map(s => (
        <FilterChip
          key={`source-${s}`}
          label={SOURCE_LABEL[s]}
          testId={`chip-source-${s}`}
          editor={
            <CheckboxList
              options={sourceOptions(showKioskSource, filterState.sources)}
              labels={SOURCE_LABEL}
              value={filterState.sources}
              onChange={v => onChange({ ...filterState, sources: v })}
              minWidth={180}
            />
          }
          onRemove={() =>
            onChange({ ...filterState, sources: filterState.sources.filter(v => v !== s) })
          }
        />
      ))}

      {filterState.date && (
        <FilterChip
          label={dateChipLabel(filterState.date, availableWindows)}
          testId="chip-window"
          editor={
            <OrderWindowPicker
              value={filterState.date}
              onChange={d => onChange({ ...filterState, date: d })}
              windows={availableWindows}
              unscheduledCount={unscheduledCount}
            />
          }
          onRemove={() => onChange({ ...filterState, date: null })}
        />
      )}

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
          onClick={() => onChange(EMPTY_FILTER_STATE)}
          data-testid="filter-clear-all"
        >
          Clear all
        </Button>
      )}
    </div>
  );
}
